import { isPostHogConfigured } from "@/lib/analytics/env";
import { isPostHogExcludedUserId } from "@/lib/analytics/excluded-users";
import { isPostHogExcludedSessionShutdown } from "@/lib/analytics/opt-out-excluded";
import { getAnalyticsSessionUserId } from "@/lib/analytics/session-user";
import { isInoraProductionHostname } from "@/lib/platform/production-hosts";

/**
 * Non-production runtimes where PostHog must stay off (local dev, Vercel preview, etc.).
 * Safe when env vars are unset — returns true and analytics stays disabled.
 */
export function isPostHogNonProductionRuntime(): boolean {
  if (process.env.NODE_ENV === "development") return true;

  const vercelEnv =
    process.env.NEXT_PUBLIC_VERCEL_ENV?.trim() ||
    process.env.VERCEL_ENV?.trim();
  if (vercelEnv === "development" || vercelEnv === "preview") return true;

  return false;
}

/**
 * Client-only: production Inora hostname on a production runtime with valid PostHog env.
 * Gates init, capture, identify, reset, session replay, and pageviews.
 */
/** Production domain + env configured (before per-user exclusion). */
export function isPostHogAnalyticsEnabled(): boolean {
  if (!isPostHogConfigured()) return false;
  if (isPostHogNonProductionRuntime()) return false;
  if (typeof window === "undefined") return false;
  return isInoraProductionHostname(window.location.hostname);
}

export function isPostHogUserExcluded(
  userId?: string | null,
): boolean {
  const resolved = userId ?? getAnalyticsSessionUserId();
  return isPostHogExcludedUserId(resolved);
}

/**
 * True only when PostHog should capture — production host and not an excluded user.
 * Excluded sessions behave as if analytics is fully disabled.
 */
export function isPostHogAnalyticsActive(): boolean {
  if (!isPostHogAnalyticsEnabled()) return false;
  if (isPostHogExcludedSessionShutdown()) return false;
  if (isPostHogUserExcluded()) return false;
  return true;
}
